import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { getPlaneImageViews } from "./fleetData";
import { useSwipe } from "./useSwipe";

const PlaneIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3.5S19 4 17.5 5.5L14 9l-8.2-1.8L3 10l7.5 3L8 16l-3-1V18l2.5 1.5L9 21h3l-1-3 3-2.5 3 7.5 2.8-2.8z" />
  </svg>
);

function FleetPlaneGallery({ plane, planeKey }) {
  const [tFleet] = useTranslation("fleet");
  const views = getPlaneImageViews(plane);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageChanging, setImageChanging] = useState(false);
  const imageChangeTimeoutRef = useRef(null);
  const galleryRef = useRef(null);
  const baseId = useId();
  const panelId = `${baseId}-panel`;
  const statusId = `${baseId}-status`;

  const planeName = plane?.fullModel || plane?.model || "avión";
  const hasMultipleImages = views.length > 1;
  const activeView = views[activeImageIndex];
  const activeTabId = `${baseId}-tab-${activeImageIndex}`;
  const totalViews = views.length;

  const runImageTransition = useCallback((applyChange) => {
    setImageChanging(true);

    if (imageChangeTimeoutRef.current) {
      clearTimeout(imageChangeTimeoutRef.current);
    }

    imageChangeTimeoutRef.current = setTimeout(() => {
      applyChange();
      requestAnimationFrame(() => {
        setImageChanging(false);
      });
      imageChangeTimeoutRef.current = null;
    }, 220);
  }, []);

  const isInitialPlaneKeyRef = useRef(true);

  useEffect(() => {
    if (isInitialPlaneKeyRef.current) {
      isInitialPlaneKeyRef.current = false;
      setActiveImageIndex(0);
      return;
    }

    runImageTransition(() => setActiveImageIndex(0));
  }, [planeKey, runImageTransition]);

  useEffect(() => {
    return () => {
      if (imageChangeTimeoutRef.current) {
        clearTimeout(imageChangeTimeoutRef.current);
      }
    };
  }, []);

  const goToView = useCallback(
    (index) => {
      if (!totalViews) return;
      const nextIndex = ((index % totalViews) + totalViews) % totalViews;
      if (nextIndex === activeImageIndex) return;

      runImageTransition(() => setActiveImageIndex(nextIndex));
    },
    [activeImageIndex, runImageTransition, totalViews]
  );

  const goToPrevious = useCallback(() => {
    goToView(activeImageIndex - 1);
  }, [activeImageIndex, goToView]);

  const goToNext = useCallback(() => {
    goToView(activeImageIndex + 1);
  }, [activeImageIndex, goToView]);

  const gallerySwipeHandlers = useSwipe({
    onSwipeLeft: goToNext,
    onSwipeRight: goToPrevious,
    enabled: hasMultipleImages && !imageChanging,
  });

  const stopPropagation = (handler) => (event) => {
    event.stopPropagation();
    handler?.(event);
  };

  const viewportSwipeHandlers = hasMultipleImages
    ? {
        onTouchStart: stopPropagation(gallerySwipeHandlers.onTouchStart),
        onTouchEnd: stopPropagation(gallerySwipeHandlers.onTouchEnd),
        onTouchCancel: stopPropagation(gallerySwipeHandlers.onTouchCancel),
      }
    : {};

  const handleKeyDown = useCallback(
    (event) => {
      if (!hasMultipleImages) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          goToPrevious();
          break;
        case "ArrowRight":
          event.preventDefault();
          goToNext();
          break;
        case "Home":
          event.preventDefault();
          goToView(0);
          break;
        case "End":
          event.preventDefault();
          goToView(totalViews - 1);
          break;
        default:
          break;
      }
    },
    [goToNext, goToPrevious, goToView, hasMultipleImages, totalViews]
  );

  if (!views.length) {
    return (
      <div
        className="fleet-plane-gallery fleet-plane-gallery--empty"
        role="img"
        aria-label={tFleet("redesign.gallery.unavailable", { name: planeName })}
      >
        <div className="placeholder-image">
          <PlaneIcon />
          <p>{tFleet("redesign.gallery.image", { model: plane.model })}</p>
        </div>
      </div>
    );
  }

  return (
    <section
      ref={galleryRef}
      className="fleet-plane-gallery"
      aria-label={tFleet("redesign.gallery.label", { name: planeName })}
      onKeyDown={handleKeyDown}
    >
      <figure className="fleet-gallery-figure">
        <div
          id={panelId}
          role="tabpanel"
          tabIndex={hasMultipleImages ? 0 : undefined}
          aria-roledescription={tFleet("redesign.gallery.roledescription")}
          aria-label={activeView?.label}
          aria-labelledby={hasMultipleImages ? activeTabId : undefined}
          className={`fleet-gallery-viewport ${imageChanging ? "is-image-changing" : ""} ${hasMultipleImages ? "fleet-gallery-viewport--swipeable" : ""}`}
          {...viewportSwipeHandlers}
        >
          {activeView ? (
            <img
              src={activeView.src}
              alt={activeView.label}
              className="plane-img fleet-gallery-main-img"
              decoding="async"
              loading="eager"
            />
          ) : null}
        </div>
      </figure>

      <p id={statusId} className="visually-hidden" aria-live="polite" aria-atomic="true">
        {activeView?.label}
        {hasMultipleImages
          ? tFleet("redesign.gallery.position", { current: activeImageIndex + 1, total: totalViews })
          : ""}
      </p>

      {hasMultipleImages && (
        <div className="fleet-gallery-controls">
          <div className="fleet-gallery-nav" aria-label={tFleet("redesign.gallery.nav")}>
            <button
              type="button"
              className="fleet-gallery-nav-btn fleet-gallery-nav-btn--secondary fleet-gallery-nav-btn--prev"
              onClick={goToPrevious}
              aria-controls={panelId}
              aria-label={tFleet("redesign.gallery.prev")}
            >
              <IoIosArrowBack size={18} aria-hidden="true" />
            </button>

            <div
              className="fleet-image-switcher"
              role="tablist"
              aria-label={tFleet("redesign.gallery.views", { name: planeName })}
            >
            {views.map((view, index) => {
              const tabId = `${baseId}-tab-${index}`;
              const isSelected = activeImageIndex === index;

              return (
                <button
                  key={`${planeKey}-view-${index}`}
                  id={tabId}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  aria-controls={panelId}
                  tabIndex={isSelected ? 0 : -1}
                  className={`fleet-image-thumb ${isSelected ? "active" : ""}`}
                  onClick={() => goToView(index)}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowRight") {
                      event.preventDefault();
                      goToView(index + 1);
                    } else if (event.key === "ArrowLeft") {
                      event.preventDefault();
                      goToView(index - 1);
                    }
                  }}
                >
                  <img src={view.src} alt="" aria-hidden="true" />
                  <span className="visually-hidden">{view.label}</span>
                </button>
              );
            })}
            </div>

            <button
              type="button"
              className="fleet-gallery-nav-btn fleet-gallery-nav-btn--secondary fleet-gallery-nav-btn--next"
              onClick={goToNext}
              aria-controls={panelId}
              aria-label={tFleet("redesign.gallery.next")}
            >
              <IoIosArrowForward size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default FleetPlaneGallery;
